import { useEffect, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Space,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { RollbackOutlined } from "@ant-design/icons";
import CTable from "components/CTable";
import useFilter from "components/Filter";
import {
  showConfirm,
  showError,
  showSuccess,
  useNewState,
} from "helpers/utility";
import useAjax from "helpers/ajax";
import { DEFAULT_PAGE_SIZE } from "constants";
import CSelect from "components/CSelect";

const LicenseTransfers = () => {
  const { get, post } = useAjax();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useNewState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [distributors, setDistributors] = useState([]);
  const [form] = Form.useForm();

  const onRollback = (record) => {
    showConfirm(
      "Do you want to rollback selected transfer?",
      "Rollback transfer"
    ).then(() => {
      post("api/v1/licenses/rollback", {
        historyID: record.historyID,
        licenseIDs: [record.licenseID],
      })
        .then((resp) => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
          showSuccess(resp.success, resp.message);
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const columns = [
    {
      title: "Actions",
      width: "65px",
      align: "center",
      render: (text, record) => (
        <Button.Group>
          <Tooltip placement="topLeft" title="Rollback">
            <Button
              type="primary"
              size="small"
              icon={<RollbackOutlined />}
              onClick={() => {
                onRollback(record);
              }}
            />
          </Tooltip>
        </Button.Group>
      ),
    },
    {
      title: "Authorization key",
      dataIndex: "authorizationKey",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Transferred to",
      dataIndex: "transferredTo",
      sorter: true,
    },
    {
      title: "Phone no",
      dataIndex: "contactNo",
      sorter: true,
    },
    {
      title: "Email address",
      dataIndex: "emailAddress",
      sorter: true,
    },
    {
      title: "Transferred date",
      dataIndex: "transferredOn",
      sorter: true,
      render: (text) =>
        text ? dayjs(text).format("MM/DD/YYYY HH:mm:ss.SSS") : "",
    },
    {
      title: "Transferred by",
      dataIndex: "transferredBy",
      sorter: true,
    },
  ];

  const getFilters = (values) => {
    const { targetCompanyID, dateFrom, dateTo } =
      values ?? form.getFieldsValue();
    return {
      targetCompanyID,
      dateFrom: dateFrom ? dayjs(dateFrom).format("MM/DD/YYYY") : null,
      dateTo: dateTo ? dayjs(dateTo).format("MM/DD/YYYY") : null,
    };
  };

  const loadData = (pager, filters, sorter = {}) => {
    const gridify = {
      page: pager.current,
      pageSize: pager.pageSize,
      sortBy: sorter.field || "transferredOn",
      sortOrder: sorter.order || "ascend",
      ...filters,
    };
    get("api/v1/licenses/transferhistory", { ...gridify })
      .then((resp) => {
        setData(resp.data);
        setPagination({
          ...pager,
          total: resp.count,
        });
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => setLoading(false));
  };

  const onTableChange = (pager, filters, sorter) => {
    const values = getFilters();
    loadData(pager, values, sorter);
  };

  const onSearch = (values) => {
    const filters = getFilters(values);
    loadData(pagination, filters);
  };

  const onClear = () => {
    form.resetFields();
    loadData(pagination);
  };

  useEffect(() => {
    get("api/v1/lookups/distributorslineage")
      .then((resp) => {
        setDistributors(resp);
      })
      .catch((err) => {
        showError(err);
      });
    loadData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ reportType: 1 }}
        onFinish={onSearch}
      >
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item label="Transferred to" name="targetCompanyID">
              <CSelect
                allowClear={true}
                dropdownMatchSelectWidth={false}
                placeholder="All"
              >
                {distributors.map((r) => (
                  <Select.Option key={r.value} value={r.value}>
                    {r.text}
                  </Select.Option>
                ))}
              </CSelect>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Date from" name="dateFrom">
              <DatePicker />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Date to" name="dateTo">
              <DatePicker />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label="&nbsp;">
              <Space>
                <Button htmlType="submit" type="primary">
                  Search
                </Button>
                <Button htmlType="button" onClick={onClear}>
                  Clear
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <CTable
        rowKey="rowID"
        columns={columns}
        dataSource={data}
        pagination={pagination}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};
export default LicenseTransfers;
